import { lazy, Suspense } from 'react';
import { Spin } from 'antd';
import styled from '@emotion/styled';
import { useConversationList, useCurrentConversationValue } from 'hooks/business/conversation';
import Empty from 'components/Empty';
import { useTranslate } from '@tolgee/react';
import {
  useAuthBeAboutExpireShops,
  useAuthInvalidShops,
} from 'hooks/business/authShops/useAuthInvalidShops';
import { ReactComponent as NoConvSvg } from './assets/noConv.svg';
import ConvList from './ConvList';
import { useShowIncentive } from 'views/Management/NewuserIncentive/state';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import IncentiveModal from 'views/Management/NewuserIncentive/IncentiveModal';
import { AdjustRelateKnowledgeDrawer } from '@knowledge/knowledges/SupplementKnowledge/AdjustRelateKnowledge';
import { ModifyOnChatDrawer } from '@knowledge/knowledges/SupplementKnowledge/ModifyOnChat';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { LightConversation } from 'components/LightConversation';
import { FullLoading } from 'components/FullLoading';
import { useReceptionBuyerStatBootstrap } from 'hooks/business/useBuyerState';
import { useReceptionConfigBootstrap } from 'views/Setting/ConversationSetting/useReceptionConfig';
import { ReceptionStatisticsHeader } from './ReceptionStatisticsHeader';

const ClassicConversation = lazy(() => import('./ClassicConversation'));

export const Container = styled.div`
  --conversation-header-height: 54px;
  --horizontal-tab-height: 40px;
  display: flex;
  width: 100%;
  height: calc(100vh - var(--header-height) - var(--horizontal-tab-height));
  overflow: hidden;
  > div {
    flex: 0 0 auto;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    > header {
      flex: 0 0 auto;
      background: #fff;
      z-index: 4;
    }
    > div {
      flex: 1 0 0;
      border-right: none;
    }
    & + div {
      border-left: 1px solid #d3d7e0;
    }
  }
`;

const EmptyCenter = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function Conversation() {
  useReceptionBuyerStatBootstrap();
  useReceptionConfigBootstrap();

  // 店铺
  useAuthInvalidShops();
  useAuthBeAboutExpireShops();

  const isLightChatLayout = useIsLightChatLayout();
  const showIncentive = useShowIncentive();

  const { NEWUSER_INCENTIVE_NOTICE } = useTriggerMapValue();

  const createTrigger = useCreateTrigger();

  const IncentiveNoticeDisplay = showIncentive && !NEWUSER_INCENTIVE_NOTICE;

  const list = useConversationList();
  const currentConversation = useCurrentConversationValue();
  const isHeaderEmpty = list.length < 1;
  const { t } = useTranslate();

  return (
    <Suspense fallback={<Spin spinning />}>
      <Container>
        <IncentiveModal
          visible={IncentiveNoticeDisplay}
          onCancel={() => createTrigger({ trigger: 'NEWUSER_INCENTIVE_NOTICE' })}
        />
        <ConvList />
        {list.length < 1 || !currentConversation ? (
          <section>
            <ReceptionStatisticsHeader isEmpty={isHeaderEmpty} />
            <EmptyCenter>
              <Empty
                description={
                  <div>{t('scene.chatWorkspace.currentConversation.empty.noneInService')}</div>
                }
                image={<NoConvSvg />}
              />
            </EmptyCenter>
          </section>
        ) : (
          <section className="relative">
            <Suspense fallback={<FullLoading />}>
              {isLightChatLayout ? (
                <LightConversation conversation={currentConversation} />
              ) : (
                <ClassicConversation conversation={currentConversation} />
              )}
            </Suspense>
          </section>
        )}
      </Container>
      <AdjustRelateKnowledgeDrawer />
      <ModifyOnChatDrawer />
    </Suspense>
  );
}

export default Conversation;
